import { useEffect, useRef, useState } from 'react'
import type { Cluster } from '../types'
import { FootageBar } from './FootageBar'
import { clipUrl, clipPartUrl, fetchClipParts, type ClipPart } from '../api'
import { useClipReady } from '../useClipReady'
import { advance, footageLayout, openingPart, pickRun, playFrom, stretchOf } from '../footage'

interface Props {
  cluster: Cluster
  /** The size of the picture's box, as it changes: where an incident seats live. */
  onBoxSize?: (size: { width: number; height: number }) => void
  /** The site said it keeps no recordings, when asked for this clip. */
  onNoRecordings?: () => void
}

// How long before the incident playback starts. Matches the pre-roll the
// server asks Frigate for, so both kinds open on the same amount of lead-in —
// and the seconds before are the evidence, so cutting to the incident exactly
// would show the hand already on the lens.
const clipLeadInSeconds = 15

// How many times a clip is requested before it is called unavailable, and how
// long between attempts. Sized against the recorder finishing its file rather
// than against a network blip: measured, a recording holding a tamper was
// finalised within 15 seconds of the act.
const clipAttempts = 5
const clipRetryMs = 3000

export function ClusterClipPanel({ cluster, onBoxSize, onNoRecordings }: Props) {
  // Each state draws its own box, so the one showing is followed as it changes.
  const [boxEl, setBoxEl] = useState<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!boxEl || !onBoxSize || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect
      onBoxSize({ width, height })
    })
    observer.observe(boxEl)
    return () => observer.disconnect()
  }, [boxEl, onBoxSize])
  const ready = useClipReady(cluster)
  // Keyed by clip so opening a different one clears the failure by derivation.
  const clipKey = `${cluster.id}:${cluster.end_time}`
  // Attempts made at this clip. A clip requested the moment it becomes
  // available can arrive before the recorder has finished writing it, and
  // latching that first failure left the panel saying the recording had
  // expired when a reload would have played it.
  const [attempts, setAttempts] = useState<{ key: string; n: number; waiting: boolean }>(
    { key: clipKey, n: 0, waiting: false },
  )
  const tries = attempts.key === clipKey ? attempts.n : 0
  const waiting = attempts.key === clipKey && attempts.waiting
  const failed = tries >= clipAttempts

  // An incident can span several recordings with real gaps between them, so
  // they are played one after another rather than stitched into a file that
  // would imply footage nobody has. A failed listing falls back to the
  // unnumbered URL, which is what this panel has always requested.
  const [parts, setParts] = useState<ClipPart[] | null>(null)
  const [at, setAt] = useState(0)
  // Which source has metadata, so the seek can wait for whichever of the two
  // it needs arrives last.
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  // Whether playback ran from one part into the next, as opposed to opening on
  // one.
  const [advanced, setAdvanced] = useState(false)
  // A recording picked, or opened on, that does not hold the incident's moment
  // plays from its own start: the moment, measured from it, lies past its end.
  const [fromStart, setFromStart] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  // The moment the incident is about, not its first second. An incident
  // labelled `tamper` routinely opens with a minute of ordinary motion before
  // anything happens — measured at 161s on one real incident. A server that
  // does not report it leaves the old behaviour.
  const moment = cluster.primary_time ?? cluster.start_time

  useEffect(() => {
    if (!ready) return
    let cancelled = false
    void (async () => {
      try {
        const p = await fetchClipParts(cluster.id)
        if (!cancelled) {
          setParts(p)
          // The part holding the moment, not simply the first: an incident
          // spanning two recordings had the tampering 161s into one and 9s
          // into the next.
          const opening = openingPart(p, moment)
          setAt(opening.at)
          setFromStart(opening.fromStart)
          setAdvanced(false)
        }
      } catch (e) {
        if (cancelled) return
        // By name, not instanceof: a test that mocks the api module whole has
        // no class to compare with, and the check threw there.
        if (e instanceof Error && e.name === 'NoRecordingsError' && onNoRecordings) onNoRecordings()
        // One part, requested the way this panel always did.
        else setParts([])
      }
    })()
    return () => { cancelled = true }
  }, [cluster.id, ready, moment, onNoRecordings])

  // Autoplay only when one part ran into the next: continuing is what the
  // dispatcher already asked for, opening is not. Tracked rather than inferred
  // from the index, because the clip can now open on a later part — the one
  // holding the moment — and that must still wait for play.
  useEffect(() => {
    if (advanced) videoRef.current?.play()?.catch(() => {})
  }, [advanced, at])

  // Several runs of footage, not several files: a dispatcher offered one
  // choice per file was offered 122 where three pieces of footage existed.
  const footage = footageLayout(parts ?? [], moment)
  const multi = parts !== null && footage.runs.length > 1
  const playingRun = parts && parts.length > 0 ? footage.runs.findIndex((r) => r.id === stretchOf(parts[at])) : -1
  // Still needed for playback, which fetches files.
  const several = parts !== null && parts.length > 1
  const src = several ? clipPartUrl(cluster.id, parts[at].index) : clipUrl(cluster.id)

  // Where in this recording the incident starts.
  //
  // An ONVIF clip is a whole recorder segment, not an export, so the incident
  // can sit deep inside it: measured on incident 5278, the part ran for eleven
  // minutes and the incident began in its last thirty seconds. Playing from
  // the file's start left a dispatcher scrubbing blind.
  //
  // Zero for a part that begins after the incident — every part after the
  // first, and a Frigate clip already exported to the window asked for.
  // Running on into the next file skips the seconds it shares with the last
  // (playFrom), only when it ran on by itself, never when opening on a part.
  const startAt = playFrom(parts ?? [], at, moment, { fromStart, ranOn: advanced, leadIn: clipLeadInSeconds })

  // Seeking needs two things that arrive independently: the video's metadata,
  // and the part listing that says where in the recording the incident is. The
  // clip is served from the same origin as the page while the listing is its
  // own request, so metadata usually wins — and seeking only on loadedmetadata
  // sought to zero and never corrected itself. That shipped, and showed as
  // 1m48s of lead-in on a real incident.
  //
  // Keyed by src so a different part seeks again rather than inheriting the
  // last one's readiness.
  // Wait before asking again, so a recorder still finishing its file is given
  // time rather than hammered. The wait also unmounts the video — the spinner
  // renders in its place — which is what makes the next attempt a fresh
  // request rather than a reused element the browser has already failed on.
  useEffect(() => {
    if (!waiting || failed) return
    const t = setTimeout(() => setAttempts((a) => ({ ...a, waiting: false })), clipRetryMs)
    return () => clearTimeout(t)
  }, [waiting, failed, tries])

  useEffect(() => {
    if (loadedFor !== src) return
    const video = videoRef.current
    if (video) video.currentTime = startAt
  }, [startAt, src, loadedFor])

  const box = 'relative w-full aspect-video max-h-[60vh] rounded-lg overflow-hidden border border-zinc-800 bg-black flex items-center justify-center'
  return (
    <div className="flex flex-col gap-4 min-w-0">
      {/* A tamper incident's clip arrives on its own evidence window rather
          than when the incident ends, so "once this incident ends" would be
          untrue for it — and it is the message shown in the seconds that
          matter most. */}
      {!ready && !cluster.all_ended && cluster.label !== 'tamper' ? (
        <div ref={setBoxEl} className={box}>
          <div className="flex flex-col items-center gap-2 text-zinc-400">
            <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full bg-fault animate-pulse motion-reduce:animate-none" />
            <span className="text-sm">Recording — clip available once this incident ends</span>
          </div>
        </div>
      ) : !ready || (waiting && !failed) ? (
        // Between attempts too. Saying "preparing" rather than "unavailable" is
        // the truthful state: the recorder may simply not have finished the file.
        <div ref={setBoxEl} className={box}>
          <div className="flex flex-col items-center gap-2 text-zinc-400">
            <div aria-hidden="true" className="w-5 h-5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin motion-reduce:animate-none" />
            <span className="text-sm">Preparing clip…</span>
          </div>
        </div>
      ) : failed ? (
        <div ref={setBoxEl} className={box}>
          <p className="text-sm text-zinc-400">Clip unavailable — recording may have expired.</p>
        </div>
      ) : (
        <div ref={setBoxEl} className={box}>
          <video
            ref={videoRef}
            key={`${cluster.id}:${multi ? parts[at].index : 'only'}`}
            src={src}
            controls
            className="w-full h-full object-contain"
            onLoadedMetadata={() => setLoadedFor(src)}
            onEnded={() => {
              // Playback runs on through the files behind a run, and on into
              // the next run, because the dispatcher asked for the footage
              // rather than for a file.
              const next = several ? advance(parts, at) : null
              if (next) {
                setAdvanced(true)
                setFromStart(next.fromStart)
                setAt(next.at)
              }
            }}
            onError={() => setAttempts({ key: clipKey, n: tries + 1, waiting: true })}
          />
          {multi && (
            <div className="absolute top-3 left-3 flex flex-wrap gap-2 pointer-events-none">
              {/* Said in words: shown only as controls, a dispatcher reads them
                  as a seam in playback rather than as separate recordings. */}
              <span className="h-6 px-2 inline-flex items-center rounded-md bg-black/65 text-[13px] font-semibold text-zinc-100">
                Recording {playingRun + 1} of {footage.runs.length}
              </span>
              {/* The gap is footage nobody has: an ONVIF recorder writes only
                  while something triggers it — 46s on one measured incident.
                  Unsaid, the clip looks complete and the missing time reads as
                  quiet. */}
              {playingRun > 0 && footage.gaps[playingRun - 1] > 0 && (
                <span className="h-6 px-2 inline-flex items-center rounded-md bg-black/65 text-[13px] text-attention">
                  {footage.gaps[playingRun - 1]}s not recorded before this
                </span>
              )}
              {playingRun < footage.runs.length - 1 && footage.gaps[playingRun] > 0 && (
                <span className="h-6 px-2 inline-flex items-center rounded-md bg-black/65 text-[13px] text-attention">
                  {footage.gaps[playingRun]}s not recorded after this
                </span>
              )}
            </div>
          )}
        </div>
      )}
      {multi && (
        <FootageBar
          footage={footage}
          playing={playingRun}
          moment={moment}
          onPick={(id) => {
            const pick = pickRun(parts, id, moment)
            setAdvanced(false)
            setFromStart(pick.fromStart)
            setAt(pick.at)
          }}
        />
      )}
    </div>
  )
}
